import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AmlCallView } from '../../phone-calls/types';
import { getLastAmlCallLocation } from '../../phone-calls/phone-calls-store';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createAmlFeatureLayer } from '../helpers/feature-layers';
import { createAmlGraphic } from '../helpers/graphics';
import type { AmlLocationGeoPointEntity } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';
import { areLatLngsEqual, isValidLatLng } from '../../location/helpers';
import { EsriMapEntityTypeEnum } from '../types';
import { useFeatureLayerVisibility } from './use-feature-layer-visibility';
import { useFeatureLayerCleanup } from './use-feature-layer-cleanup';

// show current AML (RapidSOS) icon on map

type AmlFeatureLayerProps = {
    amlCall?: AmlCallView;
    selectedAmlPhoneNumberId?: number;
    webMap?: WebMap;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    areRapidSOSAniAliLayersVisible: boolean;
};
export const useAmlFeatureLayer = ({
    amlCall,
    webMap,
    selectedAmlPhoneNumberId,
    selectedMapLedgerEntityType,
    areRapidSOSAniAliLayersVisible,
}: AmlFeatureLayerProps) => {
    const lastAmlLocation = amlCall?.locations?.length
        ? getLastAmlCallLocation(amlCall.locations)
        : undefined;
    const lastAmlLocationId = lastAmlLocation?.id;
    const lastAmlPhoneCallId = lastAmlLocation?.amlPhoneCallId;
    const lastAmlLatitude = lastAmlLocation?.latitude;
    const lastAmlLongitude = lastAmlLocation?.longitude;
    const lastAmlUncertaintyRadius = lastAmlLocation?.uncertaintyRadius;
    const lastAmlLocationTimestamp = lastAmlLocation?.locationTimestamp;
    // Inline the AML entity construction here so useMemo can depend on stable
    // primitive AML location fields. Reusing the helper would return a new
    // object on every render and defeat the memoization.
    const amlLocationGeoPointEntity = useMemo(
        () => {
            if (
                lastAmlLocationId != null &&
                lastAmlLatitude != null &&
                lastAmlLongitude != null &&
                isValidLatLng(lastAmlLatitude, lastAmlLongitude)
            ) {
                return {
                    entityId: lastAmlLocationId,
                    amlPhoneCallId: lastAmlPhoneCallId,
                    latitude: lastAmlLatitude,
                    longitude: lastAmlLongitude,
                    uncertaintyRadius: lastAmlUncertaintyRadius,
                    timestamp: lastAmlLocationTimestamp,
                };
            }

            return undefined;
        },
        [
            lastAmlLocationId,
            lastAmlPhoneCallId,
            lastAmlLatitude,
            lastAmlLongitude,
            lastAmlUncertaintyRadius,
            lastAmlLocationTimestamp,
        ]
    );

    // Keep the AML input stable so AML layer effects only re-run when the AML
    // location entity itself changes, not on every parent render.
    const aml = useMemo<AmlLocationGeoPointEntity[]>(
        () => (amlLocationGeoPointEntity ? [amlLocationGeoPointEntity] : []),
        [amlLocationGeoPointEntity]
    );
    const [amlLayer, setAmlLayer] = useState<FeatureLayer>();
    const prevSelectedAmlCallId = usePreviousLayer(selectedAmlPhoneNumberId, amlLayer);
    const prevSelectedMapLedgerEntityType = usePreviousLayer(selectedMapLedgerEntityType, amlLayer);

    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.AML>(
        EntityIdToObjectIdMaps.AML
    );
    const getAmlEntityIds = useCallback(
        (items: AmlLocationGeoPointEntity[]) => items.map((item) => item.entityId),
        []
    );

    const didAmlCallGraphicChange = useCallback(
        (currCall: AmlLocationGeoPointEntity, prevCall: AmlLocationGeoPointEntity) => {
            const isEntityTypeChanged =
                prevSelectedMapLedgerEntityType !== selectedMapLedgerEntityType &&
                (prevSelectedMapLedgerEntityType === EsriMapEntityTypeEnum.AML_CALL ||
                    selectedMapLedgerEntityType === EsriMapEntityTypeEnum.AML_CALL);

            // Force change detection if entity Id changed (new AML location)
            const entityIdChanged = currCall.entityId !== prevCall.entityId;

            return (
                isEntityTypeChanged ||
                entityIdChanged ||
                !areLatLngsEqual(currCall, prevCall) ||
                selectedAmlPhoneNumberId !== prevSelectedAmlCallId
            );
        },
        [
            prevSelectedAmlCallId,
            selectedAmlPhoneNumberId,
            prevSelectedMapLedgerEntityType,
            selectedMapLedgerEntityType,
        ]
    );

    const createAmlGraphics = useCallback(
        (amlLocationGeoPointEntities: AmlLocationGeoPointEntity[]) => {
            const amlGraphics: Graphic[] = [];
            const amlLocationGeoPointEntity =
                amlLocationGeoPointEntities[amlLocationGeoPointEntities.length - 1];

            if (amlLocationGeoPointEntity) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    amlLocationGeoPointEntity.entityId
                );
                const amlGraphic = createAmlGraphic({
                    objectId,
                    amlLocationGeoPointEntity,
                    selectedAmlPhoneNumberId,
                });
                if (amlGraphic) {
                    amlGraphics.push(amlGraphic);
                }
            }

            return amlGraphics;
        },
        [selectedAmlPhoneNumberId]
    );

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const amlFeatureLayer = createAmlFeatureLayer();
            setAmlLayer(amlFeatureLayer);
            webMap.layers.add(amlFeatureLayer);
        }
    }, [webMap]);

    useFeatureLayerVisibility({
        featureLayer: amlLayer,
        isVisible: areRapidSOSAniAliLayersVisible,
    });

    useFeatureLayerCleanup({
        layer: amlLayer,
        webMap,
        cacheKey: 'AML',
        entityIdToObjectIdMapCacheRef,
        currentItems: aml,
        cleanupStrategy: 'reset-all',
        getCurrentEntityIds: getAmlEntityIds,
    });

    useLayerControl<AmlLocationGeoPointEntity>({
        layer: amlLayer,
        currItems: aml,
        cacheKey: 'AML',
        enforceSingleFeatureInvariant: true,
        createGraphics: createAmlGraphics,
        didItemChange: didAmlCallGraphicChange,
        entityIdToObjectIdMapCacheRef,
    });

    return { amlLayer };
};
